import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASE_URL } from "./constants";

export const usersApi = createApi({
  reducerPath: "users/api",
  tagTypes: ["Users"],
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const { token } = getState().users;
      headers.set("Content-Type", "application/json");
      headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getUsersToken: build.query({
      query: () => ({
        url: `api/users/get-admin-token`,
      }),
    }),
    checkUsersLogin: build.query({
      query: (data) => ({
        url: `api/users/check-user-login`,
        params: data,
      }),
    }),
    getUsersByPassword: build.mutation({
      query: (data) => ({
        url: `api/users/login-user-by-password`,
        method: "POST",
        responseHandler: "content-type",
        body: data,
      }),
    }),
    sendRecoveryMail: build.mutation({
      query: (data) => ({
        url: `api/users/send-recovery-mail`,
        method: "POST",
        params: data,
      }),
    }),
    setNewPassword: build.mutation({
      query: (data) => ({
        url: `api/users/set-new-password`,
        method: "PUT",
        params: data,
      }),
    }),
    getProfileInfo: build.query({
      query: (token) => ({
        url: `api/users/get-user-profile-info`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      providesTags: (result, error, arg) => [{ type: "Users", id: arg }],
    }),
    getProfileInfoById: build.query({
      query: ({ token, employeeId }) => ({
        url: `api/users/get-user-profile-by-id?profileId=${employeeId}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    }),
    getUser: build.query({
      query: ({ token, employeeId }) => ({
        url: `api/users/get-user?Id=${employeeId}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    }),
    editProfileInfo: build.mutation({
      query: ({ token, data }) => ({
        url: `api/users/edit-user-profile-info`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Users", id: arg.id }],
    }),
    updateProfileInfoPassword: build.mutation({
      query: ({ token, data }) => ({
        url: `api/users/update-user-profile-password`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        method: "POST",
        body: data,
      }),
    }),
    isValidToken: build.query({
      query: () => ({
        url: `api/users/is-valid-token`,
      }),
    }),
    closeSession: build.mutation({
      query: (data) => ({
        url: `api/users/close-session`,
        // headers: {
        //   Authorization: `Bearer ${token}`,
        // },
        method: "POST",
        body: data,
      }),
    }),
    addNewUser: build.mutation({
      query: ({ token, data }) => ({
        url: `api/users/add-new-user-disp`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Users"],
    }),
    editUser: build.mutation({
      query: ({ token, data }) => ({
        url: `api/users/edit-user-disp`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Users"],
    }),
    registrateNewUser: build.mutation({
      query: (data) => ({
        url: `api/users/registrate`,
        method: "POST",
        body: data,
      }),
    }),
    getUserRolesSelect: build.query({
      query: () => ({
        url: `api/users/get-roles-select-disp`,
      }),
    }),
    getUserJobTitlesSelect: build.query({
      query: () => ({
        url: `api/users/get-job-titles-select`,
      }),
    }),
    getUsersForOrganization: build.query({
      query: ({ searchValue }) => ({
        url: `api/users/get-users-for-organization?searchValue=${searchValue}`,
      }),
    }),
    getUserGridSetting: build.query({
      query: ({ gridSettingName }) => ({
        url: `api/users/get-user-grid-setting?gridSettingName=${gridSettingName}`,
      }),
    }),
    confirmVerification: build.mutation({
      query: (data) => ({
        url: `api/users/confirm-verification`,
        method: "POST",
        body: data,
      }),
    }),
    setUserGrid: build.mutation({
      query: (data) => ({
        url: `api/users/set-user-grid-setting`,
        method: "POST",
        body: data,
      }),
    }),
    getResponsibleOrganizationsUsers: build.query({
      query: ({ searchValue, filters }) => ({
        url: `api/users/get-responsible-organizations-users?searchValue=${searchValue}${filters}`,
      }),
    }),
    startSession: build.mutation({
      query: (data) => ({
        url: `api/users/start-session`,
        method: "POST",
        body: data,
      }),
    }),
    isSessionClosed: build.query({
      query: () => ({
        url: `api/users/is-session-closed`,
      }),
    }),
    getActiveSession: build.query({
      query: (token) => ({
        url: `api/users/get-active-session`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    }),

    updateLoginData: build.mutation({
      query: (data) => ({
        url: `api/users/update-login-data`,
        method: "POST",
        body: data,
      }),
    }),

    getSubdivisionList: build.query({
      query: (params) => {
        const stringParams = Object.entries(params).reduce(
          (acc, [key, value]) => `${acc ? acc + "&" : ""}${key}=${value}`,
          ""
        );

        return {
          url: `api/users/get-subdivision-list?${stringParams}`,
        };
      },
    }),

    getArmList: build.query({
      query: (params) => {
        const stringParams = Object.entries(params).reduce(
          (acc, [key, value]) => `${acc ? acc + "&" : ""}${key}=${value}`,
          ""
        );

        return {
          url: `api/users/get-arm-list?${stringParams}`,
        };
      },
    }),

    createUpdateSubdivision: build.mutation({
      query: (data) => ({
        url: `api/users/create-update-subdivision`,
        method: "POST",
        body: data,
      }),
    }),

    getSubdivisionById: build.query({
      query: (data) => ({
        url: `api/users/get-subdivision-by-id?id=${data}`,
      }),
    }),
  }),
});

export const {
  useLazyGetSubdivisionByIdQuery,
  useCreateUpdateSubdivisionMutation,
  useGetArmListQuery,
  useLazyGetArmListQuery,
  useGetSubdivisionListQuery,
  useUpdateLoginDataMutation,
  useLazyGetActiveSessionQuery,
  useIsSessionClosedQuery,
  useStartSessionMutation,
  useGetUsersTokenQuery,
  useLazyCheckUsersLoginQuery,
  useGetProfileInfoQuery,
  useCloseSessionMutation,
  useLazyGetProfileInfoQuery,
  useConfirmVerificationMutation,
  useGetUsersByPasswordMutation,
  useGetProfileInfoByIdQuery,
  useSendRecoveryMailMutation,
  useLazyIsValidTokenQuery,
  useGetUserQuery,
  useEditUserMutation,
  useLazyGetUserGridSettingQuery,
  useGetUserGridSettingQuery,
  useSetUserGridMutation,
  useSetNewPasswordMutation,
  useEditProfileInfoMutation,
  useRegistrateNewUserMutation,
  useUpdateProfileInfoPasswordMutation,
  useAddNewUserMutation,
  useGetUserRolesSelectQuery,
  useGetUserJobTitlesSelectQuery,
  useGetUsersForOrganizationQuery,
  useGetResponsibleOrganizationsUsersQuery,
} = usersApi;
